/* eslint-disable no-nested-ternary */
import React, {useCallback} from 'react'
import {useTranslation} from 'react-i18next'
import {Divider} from '@material-ui/core'
import useLanguage from 'features/languageReport/useLanguage'
import ReportSubtitle from 'components/Text/ReportSubtitle'
import useStyles from 'components/molcules/N1SummaryReport/useStyles'
import {simpleSanitizeHTML} from 'helpers/analysisHelper'
import {ResEEGMCI5Stages} from 'api/languageApi'

interface MciProps {
  MciImagePath: string
  probability: number
  clientFirstName: string
  clientLastName: string
  footer: string
}

export default function MCIReportPageStage5API({
  MciImagePath,
  probability,
  clientFirstName,
  clientLastName,
  footer,
}: MciProps) {
  const {t} = useTranslation()
  const classes = useStyles()
  const {i18n} = useTranslation()
  const {textMciAPI} = useLanguage('mci')
  const res5Stages = textMciAPI as ResEEGMCI5Stages

  const clientFullName = (firstName: string, lastName: string): string => {
    const name = t('IGetFullName', {
      firstName: firstName ?? '',
      lastName: lastName ?? '',
    }).trim()
    return name
  }

  const getOpinion = useCallback(
    (percent?: number) => {
      if (!percent) {
        return res5Stages?.section.prediction.opinions.diagnosis ?? ''
      }
      return res5Stages?.section.prediction.opinions.diagnosis
        .replace('{0}', clientFullName(clientFirstName, clientLastName))
        .replace('{1}', String(percent))
        .replace(
          '{2}',
          percent < 50
            ? res5Stages?.section.recommendation_table.stage1.stage.description
            : percent < 60
            ? res5Stages?.section.recommendation_table.stage2.stage.description
            : percent < 70
            ? res5Stages?.section.recommendation_table.stage3.stage.description
            : percent < 85
            ? res5Stages?.section.recommendation_table.stage4.stage.description
            : res5Stages?.section.recommendation_table.stage5.stage.description,
        )
        .replace(
          '{3}',
          percent < 50
            ? '0-50'
            : percent < 60
            ? '50-60'
            : percent < 70
            ? '60-70'
            : percent < 85
            ? '70-85'
            : '>85',
        )
    },
    [probability, res5Stages],
  )
  const getOpinionContent = useCallback(
    // eslint-disable-next-line consistent-return
    (percent?: number) => {
      if (percent) {
        const recommendationTable = res5Stages?.section.recommendation_table
        if (percent < 50)
          return [
            recommendationTable?.stage1.recommendations.main,
            ...(recommendationTable?.stage1.recommendations.sub ?? []),
          ].join('\n')
        if (percent >= 50 && percent < 60)
          return [
            recommendationTable?.stage2.recommendations.main,
            ...(recommendationTable?.stage2.recommendations.sub ?? []),
          ].join('\n')
        if (percent >= 60 && percent < 70)
          return [
            recommendationTable?.stage3.recommendations.main,
            ...(recommendationTable?.stage3.recommendations.sub ?? []),
          ].join('\n')
        if (percent >= 70 && percent < 85)
          return [
            recommendationTable?.stage4.recommendations.main,
            ...(recommendationTable?.stage4.recommendations.sub ?? []),
          ].join('\n')
        if (percent >= 85)
          return [
            recommendationTable?.stage5.recommendations.main,
            ...(recommendationTable?.stage5.recommendations.sub ?? []),
          ]
        return [
          recommendationTable?.stage1.recommendations.main,
          ...(recommendationTable?.stage1.recommendations.sub ?? []),
        ].join('\n')
      }
    },
    [probability, res5Stages],
  )
  return (
    <div className={classes.mci}>
      <br />
      <ReportSubtitle className='ReportTitle' number='1'>
        {res5Stages?.section.prediction.title ?? ''}
      </ReportSubtitle>
      <p className='titleDescription' style={{color: '#4D5D6B'}}>
        {res5Stages?.section.prediction.description ?? ''}
      </p>
      <img
        src={MciImagePath}
        alt='mci report'
        style={{
          width: '100%',
          height: 'auto',
        }}
      />
      <p
        className='contentTitle'
        // @ts-ignore
        // eslint-disable-next-line
        dangerouslySetInnerHTML={{
          __html: simpleSanitizeHTML(String(getOpinion(probability))),
        }}
        style={{color: '#4D5D6B'}}
      />

      <p className='contentDescription' style={{color: '#4D5D6B'}}>
        {getOpinionContent(probability)}
      </p>

      <ReportSubtitle
        className='ReportTitle'
        style={{
          padding: '10px 5px 10px 10px',
        }}
        number='2'
      >
        {res5Stages?.section.recommendation_table.title ?? ''}
      </ReportSubtitle>
      <Divider style={{marginRight: 10, marginLeft: 10}} />
      <div className='guideTable'>
        <div className='guideCell'>
          <div className='round' style={{backgroundColor: '#15B785'}}>
            <div style={{margin: '0 0 0 8px'}}>stage</div>
            <div style={{fontSize: 30, fontWeight: 'bold'}}>1</div>
          </div>
          <div className='guideRoundTitle'>
            <div className='guideContentPercent' style={{color: '#15B785'}}>
              ~ {res5Stages?.threshold.stage1.high}%
            </div>
            <div
              style={{
                fontWeight: 'bold',
                marginTop: 5,
                marginBottom: 12,
                fontSize: 17,
              }}
              // @ts-ignore
              // eslint-disable-next-line
              dangerouslySetInnerHTML={{
                __html: simpleSanitizeHTML(
                  String(
                    res5Stages?.section.recommendation_table.stage1.stage
                      .description,
                  ),
                ),
              }}
            />
          </div>

          <div className='guideContent'>
            {i18n.language !== 'en' && (
              <div className='guideContentBold'>
                {res5Stages?.section.recommendation_table.stage1.diagnosis}
              </div>
            )}
            <div>{getOpinionContent(50)}</div>
          </div>
          <div>
            {i18n.language === 'en' && (
              <div
                style={{fontSize: 12, fontWeight: 600, padding: '0 0 10px 0 '}}
              >
                {
                  res5Stages?.section.recommendation_table.stage1
                    .recommendations.summary.fixed_message
                }
              </div>
            )}
            <div className='guideButton' style={{backgroundColor: '#15B785'}}>
              <p
                // @ts-ignore
                // eslint-disable-next-line
                dangerouslySetInnerHTML={{
                  __html: simpleSanitizeHTML(
                    String(
                      res5Stages?.section.recommendation_table.stage1
                        .recommendations.summary.box,
                    ),
                  ),
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <Divider style={{marginRight: 10, marginLeft: 10}} />
      <div className='guideTable'>
        <div className='guideCell'>
          <div className='round' style={{backgroundColor: '#85ce74'}}>
            <div style={{margin: '0 0 0 8px'}}>stage</div>
            <div style={{fontSize: 30, fontWeight: 'bold'}}>2</div>
          </div>
          <div className='guideRoundTitle'>
            <div className='guideContentPercent' style={{color: '#85ce74'}}>
              {res5Stages?.threshold.stage2.low}~
              {res5Stages?.threshold.stage2.high}%
            </div>
            <div
              style={{
                fontWeight: 'bold',
                marginTop: 5,
                marginBottom: 12,
                fontSize: 17,
              }}
              // @ts-ignore
              // eslint-disable-next-line
              dangerouslySetInnerHTML={{
                __html: simpleSanitizeHTML(
                  String(
                    res5Stages?.section.recommendation_table.stage2.stage
                      .description,
                  ),
                ),
              }}
            />
          </div>
          <div className='guideContent'>
            <div className='guideContent'>
              <div
                // @ts-ignore
                // eslint-disable-next-line
                dangerouslySetInnerHTML={{
                  __html: simpleSanitizeHTML(String(getOpinionContent(55))),
                }}
              />
            </div>
          </div>
          <div>
            {i18n.language === 'en' && (
              <div
                style={{fontSize: 12, fontWeight: 600, padding: '0 0 10px 0 '}}
              >
                {
                  res5Stages?.section.recommendation_table.stage2
                    .recommendations.summary.fixed_message
                }
              </div>
            )}
            <div className='guideButton' style={{backgroundColor: '#85ce74'}}>
              <p
                // @ts-ignore
                // eslint-disable-next-line
                dangerouslySetInnerHTML={{
                  __html: simpleSanitizeHTML(
                    String(
                      res5Stages?.section.recommendation_table.stage2
                        .recommendations.summary.box,
                    ),
                  ),
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <Divider style={{marginRight: 10, marginLeft: 10}} />
      <div className='guideTable'>
        <div className='guideCell'>
          <div className='round' style={{backgroundColor: '#ffd752'}}>
            <div style={{margin: '0 0 0 8px'}}>stage</div>
            <div style={{fontSize: 30, fontWeight: 'bold'}}>3</div>
          </div>
          <div className='guideRoundTitle'>
            <div className='guideContentPercent' style={{color: '#ffd752'}}>
              {res5Stages?.threshold.stage3.low}~
              {res5Stages?.threshold.stage3.high}%
            </div>
            <div
              style={{
                fontWeight: 'bold',
                marginTop: 5,
                marginBottom: 12,
                fontSize: 17,
              }}
              // @ts-ignore
              // eslint-disable-next-line
              dangerouslySetInnerHTML={{
                __html: simpleSanitizeHTML(
                  String(
                    res5Stages?.section.recommendation_table.stage3.stage
                      .description,
                  ),
                ),
              }}
            />
          </div>
          <div className='guideContent'>
            <div className='guideContent'>
              <div
                // @ts-ignore
                // eslint-disable-next-line
                dangerouslySetInnerHTML={{
                  __html: simpleSanitizeHTML(String(getOpinionContent(65))),
                }}
              />
            </div>
          </div>
          <div>
            {i18n.language === 'en' && (
              <div
                style={{fontSize: 12, fontWeight: 600, padding: '0 0 10px 0 '}}
              >
                {
                  res5Stages?.section.recommendation_table.stage3
                    .recommendations.summary.fixed_message
                }
              </div>
            )}
            <div className='guideButton' style={{backgroundColor: '#ffd752'}}>
              <p
                // @ts-ignore
                // eslint-disable-next-line
                dangerouslySetInnerHTML={{
                  __html: simpleSanitizeHTML(
                    String(
                      res5Stages?.section.recommendation_table.stage3
                        .recommendations.summary.box,
                    ),
                  ),
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <Divider style={{marginRight: 10, marginLeft: 10}} />
      <div className='guideTable'>
        <div className='guideCell'>
          <div className='round' style={{backgroundColor: '#fd7a41'}}>
            <div style={{margin: '0 0 0 8px'}}>stage</div>
            <div style={{fontSize: 30, fontWeight: 'bold'}}>4</div>
          </div>
          <div className='guideRoundTitle'>
            <div className='guideContentPercent' style={{color: '#fd7a41'}}>
              {res5Stages?.threshold.stage4.low}~
              {res5Stages?.threshold.stage4.high}%
            </div>
            <div
              style={{
                fontWeight: 'bold',
                marginTop: 5,
                marginBottom: 10,
                fontSize: 17,
              }}
              // @ts-ignore
              // eslint-disable-next-line
              dangerouslySetInnerHTML={{
                __html: simpleSanitizeHTML(
                  String(
                    res5Stages?.section.recommendation_table.stage4.stage
                      .description,
                  ),
                ),
              }}
            />
          </div>
          <div className='guideContent'>
            <div className='guideContent'>
              <div
                // @ts-ignore
                // eslint-disable-next-line
                dangerouslySetInnerHTML={{
                  __html: simpleSanitizeHTML(String(getOpinionContent(75))),
                }}
              />
            </div>
          </div>
          <div>
            {i18n.language === 'en' && (
              <div
                style={{fontSize: 12, fontWeight: 600, padding: '0 0 10px 0 '}}
              >
                {
                  res5Stages?.section.recommendation_table.stage4
                    .recommendations.summary.fixed_message
                }
              </div>
            )}
            <div className='guideButton' style={{backgroundColor: '#fd7a41'}}>
              <p
                // @ts-ignore
                // eslint-disable-next-line
                dangerouslySetInnerHTML={{
                  __html: simpleSanitizeHTML(
                    String(
                      res5Stages?.section.recommendation_table.stage4
                        .recommendations.summary.box,
                    ),
                  ),
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <Divider style={{marginRight: 10, marginLeft: 10}} />
      <div className='guideTable'>
        <div className='guideCell'>
          <div className='round' style={{backgroundColor: '#f24740'}}>
            <div style={{margin: '0 0 0 8px'}}>stage</div>
            <div style={{fontSize: 30, fontWeight: 'bold'}}>5</div>
          </div>
          <div className='guideRoundTitle'>
            <div className='guideContentPercent' style={{color: '#f24740'}}>
              {res5Stages?.threshold.stage5.low}% ~
            </div>
            <div
              style={{
                fontWeight: 'bold',
                marginTop: 5,
                marginBottom: 12,
                fontSize: 17,
              }}
              // @ts-ignore
              // eslint-disable-next-line
              dangerouslySetInnerHTML={{
                __html: simpleSanitizeHTML(
                  String(
                    res5Stages?.section.recommendation_table.stage5.stage
                      .description,
                  ),
                ),
              }}
            />
          </div>
          <div className='guideContent'>
            <div className='guideContent'>
              <div
                // @ts-ignore
                // eslint-disable-next-line
                dangerouslySetInnerHTML={{
                  __html: simpleSanitizeHTML(String(getOpinionContent(90))),
                }}
              />
            </div>
          </div>
          <div>
            {i18n.language === 'en' && (
              <div
                style={{fontSize: 12, fontWeight: 600, padding: '0 0 10px 0 '}}
              >
                {
                  res5Stages?.section.recommendation_table.stage5
                    .recommendations.summary.fixed_message
                }
              </div>
            )}
            <div className='guideButton' style={{backgroundColor: '#f24740'}}>
              <p
                // @ts-ignore
                // eslint-disable-next-line
                dangerouslySetInnerHTML={{
                  __html: simpleSanitizeHTML(
                    String(
                      res5Stages?.section.recommendation_table.stage5
                        .recommendations.summary.box,
                    ),
                  ),
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <Divider style={{marginRight: 10, marginLeft: 10, marginBottom: 10}} />
      <div className={classes.footer}>{footer}</div>
    </div>
  )
}
